import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Share, PlusSquare, CheckCircle2, MonitorSmartphone } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { usePwaInstall } from "../../lib/pwa";

// Botão "Instalar aplicação" — prompt nativo quando disponível; senão, instruções (iOS / outros browsers)
export const InstallAppButton = ({ variant = "outline", size = "sm", className = "", asMenuItem = false }) => {
  const { installed, canPrompt, ios, install } = usePwaInstall();
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => {
      toast("Nova versão do Lumora disponível", {
        action: { label: "Atualizar", onClick: () => e.detail?.waiting?.postMessage("SKIP_WAITING") },
        duration: 15000,
      });
    };
    window.addEventListener("lumora:sw-update", onUpdate);
    return () => window.removeEventListener("lumora:sw-update", onUpdate);
  }, []);

  const onClick = async () => {
    if (installed) return toast.success("O Lumora já está instalado neste dispositivo");
    if (canPrompt) {
      const outcome = await install();
      if (outcome === "accepted") toast.success("Lumora instalado! Abra-o a partir do ecrã principal / ambiente de trabalho");
      return;
    }
    setHelp(true);
  };

  const label = installed ? "Aplicação instalada" : "Instalar aplicação";
  const Icon = installed ? CheckCircle2 : Download;
  return (
    <>
      {asMenuItem ? (
        <button type="button" onClick={onClick} className={`flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-muted ${className}`} data-testid="install-app-btn">
          <Icon className="w-4 h-4 mr-2" /> {label}
        </button>
      ) : (
        <Button type="button" variant={variant} size={size} onClick={onClick} className={`gap-2 ${className}`} data-testid="install-app-btn">
          <Icon className="w-4 h-4" /> {label}
        </Button>
      )}

      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-md" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MonitorSmartphone className="w-5 h-5 text-cyan-500" /> Instalar o Lumora como aplicação</DialogTitle>
            <DialogDescription>Funciona como uma app: ícone no ecrã principal, ecrã inteiro sem barra do navegador e abertura rápida.</DialogDescription>
          </DialogHeader>
          {ios ? (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3"><span className="font-bold text-cyan-500">1.</span><span>No Safari, toque em <Share className="inline w-4 h-4 mx-1" /> <b>Partilhar</b> (barra inferior).</span></li>
              <li className="flex gap-3"><span className="font-bold text-cyan-500">2.</span><span>Escolha <PlusSquare className="inline w-4 h-4 mx-1" /> <b>Adicionar ao ecrã principal</b>.</span></li>
              <li className="flex gap-3"><span className="font-bold text-cyan-500">3.</span><span>Confirme em <b>Adicionar</b>. O Lumora aparece como uma app.</span></li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3"><span className="font-bold text-cyan-500">1.</span><span><b>Chrome / Edge (computador):</b> clique no ícone de instalação <Download className="inline w-4 h-4 mx-1" /> na barra de endereço, ou no menu ⋮ → <b>Instalar Lumora</b>.</span></li>
              <li className="flex gap-3"><span className="font-bold text-cyan-500">2.</span><span><b>Android (Chrome):</b> menu ⋮ → <b>Instalar aplicação</b> / <b>Adicionar ao ecrã principal</b>.</span></li>
              <li className="flex gap-3"><span className="font-bold text-cyan-500">3.</span><span>Se a opção não aparecer, recarregue a página e tente novamente — alguns navegadores só a mostram após a primeira visita.</span></li>
            </ol>
          )}
          <div className="flex justify-end pt-2"><Button onClick={() => setHelp(false)} data-testid="install-help-close">Percebi</Button></div>
        </DialogContent>
      </Dialog>
    </>
  );
};
